import type { SupabaseClient } from "@supabase/supabase-js";
import { resolveWorkspaceMemberLabel } from "@/lib/workspace-member-display";

export type LeadAssigneeInfo = {
  memberIds: string[];
  label: string | null;
};

async function fetchMemberLabelMap(
  client: SupabaseClient,
  memberIds: string[],
  workspaceId: string,
): Promise<Map<string, string>> {
  const labelByMemberId = new Map<string, string>();
  if (memberIds.length === 0) return labelByMemberId;

  const { data: members, error: membersError } = await client
    .from("workspace_members")
    .select("id, display_name, user_id")
    .in("id", memberIds)
    .eq("workspace_id", workspaceId);

  if (membersError) {
    console.error("Error fetching workspace members for lead assignees:", membersError);
    return labelByMemberId;
  }

  const userIds = [...new Set((members ?? []).map((member) => member.user_id).filter(Boolean))];
  const { data: profiles, error: profilesError } =
    userIds.length > 0
      ? await client.from("profiles").select("id, full_name").in("id", userIds)
      : { data: [], error: null };

  if (profilesError) {
    console.error("Error fetching profiles for lead assignees:", profilesError);
    return labelByMemberId;
  }

  const profileByUserId = new Map((profiles ?? []).map((profile) => [profile.id, profile.full_name]));
  for (const member of members ?? []) {
    labelByMemberId.set(
      member.id,
      resolveWorkspaceMemberLabel(member.display_name, profileByUserId.get(member.user_id)),
    );
  }

  return labelByMemberId;
}

/** Resolve assignees de vários leads em batch (junction table + fallback primário). */
export async function fetchLeadAssigneeInfoByLeadIds(
  client: SupabaseClient,
  leadIds: string[],
  workspaceId: string,
): Promise<Map<string, LeadAssigneeInfo>> {
  const result = new Map<string, LeadAssigneeInfo>();
  if (leadIds.length === 0) return result;

  const { data: assignmentRows, error: assignmentError } = await client
    .from("lead_member_assignments")
    .select("lead_id, member_id, created_at")
    .in("lead_id", leadIds)
    .order("created_at", { ascending: true });

  if (assignmentError) {
    console.error("Error fetching lead_member_assignments:", assignmentError);
  }

  const assigneesByLeadId = new Map<string, string[]>();
  for (const row of assignmentRows ?? []) {
    const list = assigneesByLeadId.get(row.lead_id) ?? [];
    list.push(row.member_id);
    assigneesByLeadId.set(row.lead_id, list);
  }

  const missingLeadIds = leadIds.filter((id) => !assigneesByLeadId.has(id));
  if (missingLeadIds.length > 0) {
    const { data: leadRows, error: leadsError } = await client
      .from("leads")
      .select("id, assigned_member_id")
      .in("id", missingLeadIds);

    if (leadsError) {
      console.error("Error fetching lead primary assignees:", leadsError);
    } else {
      for (const row of leadRows ?? []) {
        if (row.assigned_member_id) {
          assigneesByLeadId.set(row.id, [row.assigned_member_id]);
        }
      }
    }
  }

  const allMemberIds = [...new Set([...assigneesByLeadId.values()].flat())];
  const labelByMemberId = await fetchMemberLabelMap(client, allMemberIds, workspaceId);

  for (const leadId of leadIds) {
    const memberIds = assigneesByLeadId.get(leadId) ?? [];
    const labels = memberIds
      .map((id) => labelByMemberId.get(id))
      .filter((label): label is string => Boolean(label));
    result.set(leadId, {
      memberIds,
      label: labels.length > 0 ? labels.join(", ") : null,
    });
  }

  return result;
}
